import type { MachineField } from "./machine.ts";

/**
 * What a benchmark's GPU renderer string says for certain, and the warning a report carries for it (P2.9-T19, R-0166).
 * A page cannot learn which graphics cards a machine holds, only the one the browser drew with, so nothing here
 * guesses that a faster card exists. Two things are knowable from the string alone: it names Intel's integrated
 * graphics (UHD, HD or Iris), or it names a software rasteriser that draws on the processor. Every other string, a
 * hidden one included, is not judged. Intel Arc is left out because it is sold both as a card and built in, plain
 * "Intel(R) Graphics" because the name says no more, and AMD's built-in graphics because their names do not tell them
 * from a card's. The two sentences `rendererWarning` returns are drafts for the author to word; they are listed in the
 * Playbook's row P2.9-T19.
 */

/**
 * What a renderer string says: drawn in software, drawn by integrated graphics, or nothing certain.
 */
export type RendererConcern = "software" | "integrated" | "none";

/**
 * The key of the machine block's line that holds the renderer string.
 */
export const RENDERER_FIELD = "GPU renderer";

/**
 * The software rasterisers a browser falls back to: SwiftShader (Chromium), llvmpipe, softpipe and lavapipe (Mesa),
 * the Microsoft Basic Render Driver (WARP), and any string that calls itself a software renderer.
 */
const SOFTWARE = /swiftshader|llvmpipe|softpipe|lavapipe|basic render driver|software (?:rasterizer|renderer|adapter)/i;

/**
 * Intel's integrated graphics by their product names: "UHD Graphics", "HD Graphics" and every Iris.
 */
const INTEGRATED = /intel.*\b(?:u?hd graphics|iris)\b/i;

/**
 * Classifies a renderer string as WebGL unmasks it.
 *
 * @param renderer The string, or what the machine block prints when the browser gives none.
 * @returns "software" for a software rasteriser, "integrated" for Intel UHD, HD or Iris graphics, else "none".
 */
export function rendererConcern(renderer: string): RendererConcern {
  if (SOFTWARE.test(renderer)) {
    return "software";
  }
  return INTEGRATED.test(renderer) ? "integrated" : "none";
}

/**
 * The warning for a renderer string.
 *
 * @param renderer The string.
 * @returns One line for the top of the report and the panel, or null when the string gives no cause for one.
 */
export function rendererWarning(renderer: string): string | null {
  const concern = rendererConcern(renderer);
  if (concern === "software") {
    return `WARNING: this ran on software rendering (${renderer}), with no graphics card at all, so these figures say nothing about this machine's GPU. Turn on hardware acceleration in the browser's settings, update the graphics driver and run again.`;
  }
  if (concern === "integrated") {
    return `This ran on ${renderer}. If this machine has a faster graphics card, set the browser to High performance in the system's graphics settings (Windows: Settings, System, Display, Graphics) and run again.`;
  }
  return null;
}

/**
 * The warning for a report's machine block, read from its renderer line.
 *
 * @param machine The block.
 * @returns The warning, or null when the block has no renderer line or the line gives no cause for one.
 */
export function machineWarning(machine: readonly MachineField[]): string | null {
  const renderer = machine.find((field) => field.key === RENDERER_FIELD)?.value;
  return renderer === undefined ? null : rendererWarning(renderer);
}
